// src/context/SocketContext.js
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { initWebSocket, SOCKET } from './initWebSocket';
import { useDispatch, useSelector } from 'react-redux';
import {
  setBotWaitLoader,
  setBotWaitLoaderWithShimmer,
  setButtonLoader,
  setSearchLoader
} from '../../state/actions/loader';
import { processMessageQueue } from './messagesMiddleware';
import { getAuthData, getSessinIdFromLS } from '../storage/LocalStorage';
import { createConversationId } from '../../utils/conversationUtils';
import { appType } from '../../utils/config/configHelpers'
import { getPageName } from '../../utils/general';
import { setInializeSocket } from '../../state/actions/socket';
import { hangupCall, incomingVoipCall } from '../../state/actions/phone';
import { currentTimeStamp } from '../../utils/moment';
import { pushAsyncMsg } from '../bot/InteractionsService';

const SocketContext = createContext(null);

export const SocketProvider = ({ lastLoggedInDomain, children }) => {
  const [socketParams, setSocketParams] = useState(null);
  const [connected, setConnected] = useState(false);
  const messageQueue = useRef([]);
  const intervalRef = useRef(null);

  const dispatch = useDispatch();
  const pathname = getPageName();
  const sessionIdfromLS = getSessinIdFromLS();

  const userDetail = getAuthData();
  const userData = userDetail?.user || {};
  const sessionId = sessionIdfromLS || userDetail.auth?.sessionId;

  const selectedBotDetails = useSelector((state) => state.domain.selectedBotDetails);
  const selectedDomainDetails = useSelector((state) => state.domain.selectedDomainDetails);
  const isDefaultHomePageBot = useSelector((state) => state.bot.isDefaultHomePageBot);
  const initializeSocket = useSelector((state) => state.socket.initializeSocket);

  useEffect(() => {
    dispatch(setInializeSocket(true));
  }, [])

  // Initialize WebSocket
  const initializeWebSocket = async () => {
    const params = {
      email: userData.emailAddress || "",
      sessionId: sessionId,
      appType: appType,
      platform: "web",
    };

    console.log("params sent to initWebSocket", params);

    initWebSocket(params, sessionId);
    setSocketParams(params);
    dispatch(setInializeSocket(false));
  };

  useEffect(() => {
    if (userData && Object.keys(userData).length > 0 && initializeSocket) {
      initializeWebSocket();
    }
  }, [userData]);


  // Initialize WebSocket for bot chat
  const initializeWebSocketForBotChat = async () => {
    const currentDomain = selectedDomainDetails?.userDomain || lastLoggedInDomain?.userDomain || '';
    const landingBotId = selectedBotDetails?.botId || lastLoggedInDomain?.landingBotId || '';

    if (!landingBotId) {
      console.log("%c Unable to get last login landingBotId", "color: red;");
      dispatch(setBotWaitLoader(false));
      return;
    }

    const conversationId = await createConversationId(userData.userId, landingBotId);
    const params = {
      email: userData.emailAddress || "",
      sessionId: sessionId,
      appType: appType,
      platform: "web",
      conversationId,
      botId: landingBotId,
      userDomain: currentDomain
    };

    console.log("params sent to initWebSocket for bot chat", params, currentTimeStamp());

    initWebSocket(params, sessionId, true);
    setSocketParams(params);
    localStorage.setItem("botParams", JSON.stringify(params));
  };

  useEffect(() => {
    if (selectedBotDetails && !selectedBotDetails.conversational && !selectedBotDetails.systemBot && pathname === "Home" && !isDefaultHomePageBot) {
      initializeWebSocketForBotChat();
    }
  }, [selectedBotDetails, lastLoggedInDomain]);

  useEffect(() => {
    const socket = SOCKET.socketClient;
    if (!socket || !socketParams?.email) return;

    const receiveIncoming = (message) => {
      pushAsyncMsg(message);
      if (message) {
        return doActionBasedOnContentType(message);
      }
    };

    const doActionBasedOnContentType = (message) => {
      let contentType = parseInt(message.contentType);
      switch (contentType) {
        case 11000:
          //Message type wallet balance
          //updateWalletBalance(message);
          break;
        case 25000:
          dispatch(incomingVoipCall(message))
          break;
        case 14001:
        case 14002:
          // This resets unread messages count across all devices when user reads chat in any 1 device
          //resetUnreadMessagesCount(message);
          break;
        case 25010:
          dispatch(hangupCall(message))
          //resetCallInfo();
          break;
        case 10002:
        case 10001:
        case 10003:
        case 10004:
        case 10005:
        case 10007:
        case 1001:
          // updateContactList(message);
          break;
        case 11001:
        case 11002:
          //  updateChannelList(message);
          break;
        default:
          return;
      }
    };

    const handleMessage = (data) => {
      const botId = data?.data?.bot;
      receiveIncoming(data?.data);

      if (botId == "im-bot") {
        const { details, contentType, createdOn, createdBy, messageId } = data.data;
        const messageObj = details?.[0];
        if (messageObj.messageType && messageObj.messageType === "Runmode") return;
        const options = { createdBy };

        messageQueue.current.push({
          messageObj: {
            messageId,
            messageType: contentType.toString(),
            createdBy,
            options,
            msg: messageObj.message
          },
          createdOn
        });
      };


      if (botId === socketParams.botId) {
        // Dispatch all loaders off after 300ms
        setTimeout(() => {
          dispatch(setBotWaitLoader(false));
          dispatch(setButtonLoader(false));
          dispatch(setSearchLoader(false));
          dispatch(setBotWaitLoaderWithShimmer(false));
        }, 600);

        // Push message into the queue
        messageQueue.current.push({
          messageObj: data.data,
          createdOn: data.data.createdOn,
        });
      }
    };
    // Setup socket listener
    socket.off(socketParams.email, handleMessage);
    socket.on(socketParams.email, handleMessage);

    // Setup interval for queue processing
    intervalRef.current = setInterval(() => {
      processMessageQueue({ messageQueue });
    }, 300);

    setConnected(true);
    return () => {
      clearInterval(intervalRef.current);
      socket.off(socketParams.email);
      setConnected(false);
    };
  }, [socketParams?.email, socketParams?.botId, SOCKET.socketClient]);

  // Manual disconnect function
  const disconnectSocket = () => {
    const socket = SOCKET.socketClient;
    if (socket) {
      // Remove listeners for current email
      if (socketParams?.email) {
        socket.off(socketParams.email);
      }
      // Clear interval that processes the queue
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      // Close the socket connection
      if (socket.connected) {
        socket.disconnect();
      }
      setConnected(false);
      console.log("Socket manually disconnected");
    }
  };

  return (
    <SocketContext.Provider value={{ socket: SOCKET.socketClient, connected, disconnectSocket }}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => useContext(SocketContext);
